import { ROUTES } from '@shared/configs/constants';
import { ProfileInfoCard } from './components';
import { Button } from '@shared/components/common/Button';
import { PencilLine } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useStore } from '@shared/hooks/useStore';
import { ChangePasswordPage } from '@user/pages';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { Gender, WorkForm } from '@shared/types/enums';
import { GENDER_CONFIG, WORK_FORM_CONFIG, WORK_TYPE } from '@shared/components/constants/select.constant';
import Heading from '@/shared/components/common/Heading';
import { formatDateTime } from '@shared/utils/datetime';
import { DATE_YMD } from '@shared/components/constants/common.constants';

export default observer(function ProfilePage() {
    const {
        modalStore: { showModal },
        authStore: { profile }
    } = useStore();
    const isChangeEmailPending = useMemo(() => !!profile?.newEmail, [profile]);

    const showChangePassword = () => {
        showModal({
            id: 'form-change-password',
            size: '2xl',
            showHeader: false,
            footerClassName: 'hidden',
            contentClassName: 'p-0 rounded-lg md:p-0 w-[calc(100%-2rem)]',
            content: <ChangePasswordPage />
        });
    };

    // 個人情報
    const profileFields = [
        { label: '名前', value: profile?.fullName || '' },
        { label: '名前 (フリガナ)', value: profile?.fullNameFuri || '' },
        { label: '性別', value: GENDER_CONFIG[profile?.gender as Gender]?.label || '' },
        { label: '生年月日', value: formatDateTime(profile?.birthday, DATE_YMD, { returnDefault: '' }) || '' },
        { label: 'メールアドレス', value: profile?.email || '', helperText: isChangeEmailPending ? `∗ メールアドレスを${profile?.newEmail}に変更するように要求しました 。新しいメールアドレスの確認リンクを記載したメールが届きます。再ログインする前に、このメールを開いてリンクをクリックしてください。` : '' },
        ...(!profile?.isSynchronizedRecord ?
            [{
                label: 'パスワード',
                value: 'パスワード変更',
                labelClassName: 'cursor-pointer font-semibold text-[#009ACD] underline-offset-2 hover:underline',
                onClick: showChangePassword
            }] :
            []),
        { label: 'お客様番号', value: profile?.customerCode || '' }
    ];

    // 薬剤師情報
    const pharmacistFields = [
        { label: '薬剤師名簿登録番号', value: profile?.pharmacistNumber || '' },
        { label: '沖/外選択', value: WORK_FORM_CONFIG[profile?.workForm as WorkForm]?.label || '' },
        { label: '法人名', value: profile?.companyName || '' },
        { label: '法人名（フリガナ）', value: profile?.companyNameFuri || '' },
        { label: '勤務業態', value: WORK_TYPE[profile?.workType as keyof typeof WORK_TYPE] || profile?.workType || '' }
    ];

    // 連絡先情報
    const contactFields = [
        { label: '郵便番号', value: profile?.zipCode || '' },
        { label: '都道府県', value: profile?.prefecture || '' },
        { label: '住所', value: `${profile?.address1 ?? ''} ${profile?.address2 ?? ''}` },
        { label: '電話番号', value: profile?.phoneNumber || '' }
    ];

    return (
        <section className='flex flex-col gap-4 md:gap-6'>

            <Heading variant='h2' className='text-center md:hidden'>
                アカウント情報
            </Heading>
            <div className='flex gap-4 sm:justify-end justify-center'>
                {/* change password */}
                <Link to={ROUTES.profileEdit.href}>
                    <Button className='gap-2'>
                        <PencilLine size={16} />
                        プロフィール編集
                    </Button>
                </Link>
            </div>
            <ProfileInfoCard title='個人情報' fields={profileFields} />
            <ProfileInfoCard title='薬剤師情報' fields={pharmacistFields} />
            <ProfileInfoCard title='連絡先情報' fields={contactFields} />
        </section>
    );
});
